import React from 'react';
import { isEmpty } from 'lodash';
import { addDays } from 'date-fns';

import { BLACKLIST_NUMBER_VALUES } from '../../../../core/constants';
import { DateState, ErrorState, FormState, PriceVariant, PriceVariantErrorState } from './types';
import { invalidNumberMessage, invalidQuantityMessage, invalidSellingPriceMessage } from './unit-method';

export const isValidNumberValue = (name: string, value: string): boolean => {
  if (name !== 'quantity') {
    return !isNaN(parseFloat(value));
  }

  const parsed = parseInt(value, 10);

  return value !== '' && !BLACKLIST_NUMBER_VALUES.includes(value) && parsed >= 1 && !isNaN(parsed);
};

/* eslint-disable complexity */
export const validate = (
  price_variants: PriceVariant[],
  missing_end_date_for_last_variant: boolean,
  setMissingEndDateForLastVariant: (value: React.SetStateAction<boolean>) => void,
  validatePriceChange?: boolean
): FormState => {
  const errorStates: ErrorState = {};
  const priceVariantErrorList: PriceVariantErrorState<PriceVariant>[] = [];
  const priceVariantErrors: PriceVariantErrorState<PriceVariant> = {};

  // if (vat_percentage && isNaN(parseFloat(vat_percentage))) {
  //   errorStates.vat_percentage = invalidNumberMessage;
  // }

  price_variants.forEach((price_variant, index) => {
    const errorState: PriceVariantErrorState<PriceVariant> = {};

    if (!isValidNumberValue('selling_price', price_variant.selling_price)) {
      errorState.selling_price = invalidSellingPriceMessage;
      priceVariantErrors.selling_price = invalidSellingPriceMessage;
    }

    if (!isValidNumberValue('quantity', price_variant.quantity)) {
      errorState.quantity = invalidQuantityMessage;
      priceVariantErrors.quantity = invalidQuantityMessage;
    }

    if (!isValidNumberValue('growth', price_variant.growth) || Number(price_variant.growth) > 100) {
      errorState.growth = invalidNumberMessage;
      priceVariantErrors.growth = invalidNumberMessage;
    }

    if (!isValidNumberValue('refund', price_variant.refund) || Number(price_variant.refund) > 100) {
      errorState.refund = invalidNumberMessage;
      priceVariantErrors.refund = invalidNumberMessage;
    }

    const lastIndex = price_variants.length - 1;

    if (missing_end_date_for_last_variant && index === lastIndex && price_variants[lastIndex].end_date !== null) {
      setMissingEndDateForLastVariant(false);
    }

    if (validatePriceChange && index === lastIndex && price_variants[lastIndex].end_date === null) {
      setMissingEndDateForLastVariant(true);
    }

    priceVariantErrorList.push(errorState);
  });

  if (!isEmpty(priceVariantErrors)) {
    errorStates.price_variants = priceVariantErrorList;
  }

  return {
    errors: errorStates,
    price_variant_errors: priceVariantErrors
  };
};

export const formatPendingData = (price_variants: PriceVariant[]) => {
  const new_price_variants = price_variants.map(price_variant => {
    return {
      ...price_variant,
      // added Number due the type from sdk-predict has changed
      quantity: Number(price_variant.quantity)
    };
  });

  return {
    price_variants: new_price_variants
  };
};

export const getNextDay = (date: Date) => {
  const newDate = new Date(date);

  return addDays(newDate, 1);
};

export const calculateDateChanges = (
  name: keyof DateState,
  index: number,
  date: Date,
  price_variants: PriceVariant[]
): { [index: number]: PriceVariant }[] => {
  const currentPriceVariant = price_variants[index];
  let newPriceVariants: { [index: number]: PriceVariant }[] = [
    {
      [index]: {
        ...currentPriceVariant,
        [name]: date
      }
    }
  ];

  if (name === 'end_date' && price_variants.length > 1 && index !== price_variants.length - 1) {
    const nextPriceVariant = price_variants[index + 1];
    const nextStartDate = getNextDay(date);
    let nextEndDate = nextPriceVariant.end_date;

    if (nextEndDate && nextEndDate <= nextStartDate) {
      nextEndDate = getNextDay(nextStartDate);

      const priceVariantUpdates = calculateDateChanges('end_date', index + 1, nextEndDate, price_variants);

      priceVariantUpdates[0][index + 1].start_date = nextStartDate;
      newPriceVariants = [...newPriceVariants, ...priceVariantUpdates];
    } else {
      newPriceVariants.push({
        [index + 1]: {
          ...nextPriceVariant,
          start_date: nextStartDate,
          end_date: nextEndDate
        }
      });
    }
  }

  if (name === 'start_date' && currentPriceVariant.end_date && currentPriceVariant.end_date <= date) {
    const priceVariantUpdates = calculateDateChanges('end_date', index, getNextDay(date), price_variants);

    priceVariantUpdates[0][index].start_date = date;
    newPriceVariants = priceVariantUpdates;
  }

  return newPriceVariants;
};
